import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Download, Upload, ExternalLink, Database, Share2, FileText, CheckCircle2, Trash2, MessageSquare, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { SectionHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { exportBackup, importBackup, getSettings, uploadCarePdf, deleteCarePdf, carePdfPublicUrl, updateMessageTemplates } from "@/lib/api";
import { fileToBase64, formatDate, DEFAULT_TEMPLATES } from "@/lib/format";
import { confirmAction } from "@/lib/confirm";

const MESSAGE_FIELDS = [
  {
    key: "appointment",
    label: "Confirmação de Agendamento",
    description: "Enviada após cadastrar uma nova sessão de tatuagem.",
    vars: ["{nome}", "{data}", "{descricao}", "{local}", "{valor}", "{sinal}", "{saldo}"],
    rows: 9,
  },
  {
    key: "birthday",
    label: "Aniversário",
    description: "Enviada para parabenizar a cliente no aniversário.",
    vars: ["{nome}"],
    rows: 3,
  },
  {
    key: "followup",
    label: "Pós-Atendimento (cicatrização)",
    description: "Enviada 7 dias depois da sessão para acompanhar a cicatrização.",
    vars: ["{nome}"],
    rows: 3,
  },
  {
    key: "care",
    label: "Cuidados Pós-Tatuagem",
    description: "Enviada com o link do PDF de cuidados ao clicar no botão de cuidados na sessão.",
    vars: ["{nome}", "{link}"],
    rows: 5,
  },
  {
    key: "general",
    label: "Mensagem rápida (genérica)",
    description: "Disparada pelo botão WhatsApp no perfil do cliente.",
    vars: ["{nome}"],
    rows: 2,
  },
];

export default function Settings() {
  const [importing, setImporting] = useState(false);
  const [replace, setReplace] = useState(false);
  const [settings, setSettings] = useState({ has_care_pdf: false });
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [messages, setMessages] = useState(DEFAULT_TEMPLATES);
  const [savingMsg, setSavingMsg] = useState(false);
  const fileRef = useRef(null);
  const pdfRef = useRef(null);

  const loadSettings = () => getSettings().then((s) => {
    setSettings(s);
    if (s.message_templates) {
      setMessages({ ...DEFAULT_TEMPLATES, ...s.message_templates });
    }
  }).catch(() => {});

  useEffect(() => { loadSettings(); }, []);

  const doExport = async () => {
    try {
      const data = await exportBackup();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `julliet-ink-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup exportado");
    } catch {
      toast.error("Erro ao exportar");
    }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const r = await importBackup(payload, replace);
      const total = Object.values(r.counts || {}).reduce((a, b) => a + b, 0);
      toast.success(`Importação concluída — ${total} registros`);
      loadSettings();
    } catch (err) {
      toast.error("Arquivo de backup inválido");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doPdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Por favor selecione um arquivo PDF");
      if (pdfRef.current) pdfRef.current.value = "";
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("PDF maior que 6 MB");
      if (pdfRef.current) pdfRef.current.value = "";
      return;
    }
    setUploadingPdf(true);
    try {
      const b64 = await fileToBase64(file);
      await uploadCarePdf(b64, file.name);
      toast.success("PDF de cuidados atualizado");
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar PDF");
    } finally {
      setUploadingPdf(false);
      if (pdfRef.current) pdfRef.current.value = "";
    }
  };

  const doPdfDelete = async () => {
    if (!(await confirmAction("Remover o PDF de cuidados?"))) return;
    await deleteCarePdf();
    toast.success("Removido");
    loadSettings();
  };

  const rankingUrl = `${window.location.origin}/ranking`;
  const carePdfUrl = carePdfPublicUrl();
  const pdfSizeKB = settings.care_pdf_size ? Math.round(settings.care_pdf_size / 1024) : 0;

  const saveMessages = async () => {
    setSavingMsg(true);
    try {
      await updateMessageTemplates(messages);
      toast.success("Mensagens atualizadas");
    } catch {
      toast.error("Erro ao salvar mensagens");
    } finally {
      setSavingMsg(false);
    }
  };

  const resetMessage = (key) => {
    setMessages({ ...messages, [key]: DEFAULT_TEMPLATES[key] });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <SectionHeader
        eyebrow="Sistema"
        title="Configurações"
        description="Cuide dos seus dados, do PDF de cuidados e do link público do estúdio."
      />

      {/* Care PDF */}
      <div className="elegant-card p-8 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#F4E8D6] flex items-center justify-center">
            <FileText className="w-5 h-5 text-[#C4A77D]" strokeWidth={1.5} />
          </div>
          <h2 className="font-serif-display text-2xl text-[#2E332C]">PDF de Cuidados</h2>
        </div>
        <p className="text-sm text-[#5C6658] leading-relaxed mb-6">
          Faça upload do seu PDF com instruções de cuidado pós-tatuagem. Ele ficará disponível por um link público que
          você pode enviar para suas clientes pelo botão "Cuidados" em cada sessão (WhatsApp).
        </p>

        {settings.has_care_pdf ? (
          <div className="rounded-xl bg-[#F5F2E8] p-4 mb-4 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#7B8B6F] shrink-0" />
                <p className="text-sm text-[#2E332C] font-medium truncate">{settings.care_pdf_filename || "cuidados.pdf"}</p>
              </div>
              <p className="text-xs text-[#5C6658] mt-1">
                {pdfSizeKB} KB · enviado em {settings.care_pdf_uploaded_at ? formatDate(settings.care_pdf_uploaded_at) : ""}
              </p>
              <p className="text-xs text-[#5C6658] mt-2 truncate" data-testid="care-pdf-url">{carePdfUrl}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button asChild variant="outline" size="sm" className="rounded-full border-[#E8E4D9]" data-testid="open-care-pdf">
                <a href={carePdfUrl} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5" /></a>
              </Button>
              <Button variant="outline" size="sm" onClick={doPdfDelete} className="rounded-full border-[#B86B6B]/40 text-[#B86B6B] hover:bg-[#FCEAEA]" data-testid="delete-care-pdf">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl bg-[#FCEAEA]/30 border border-dashed border-[#B86B6B]/30 p-4 mb-4">
            <p className="text-sm text-[#5C6658]">Nenhum PDF cadastrado ainda.</p>
          </div>
        )}

        <div>
          <Label className="text-xs uppercase tracking-wider text-[#5C6658]">
            {settings.has_care_pdf ? "Substituir PDF" : "Enviar PDF"}
          </Label>
          <Input
            ref={pdfRef}
            type="file"
            accept="application/pdf"
            onChange={doPdfUpload}
            disabled={uploadingPdf}
            className="mt-2 bg-white"
            data-testid="care-pdf-upload"
          />
          <p className="text-xs text-[#5C6658] mt-2 italic">Limite: 6 MB. Apenas .pdf.</p>
        </div>
      </div>

      {/* Message Templates */}
      <div className="elegant-card p-8 mb-6">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#E5EBE0] flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-[#7B8B6F]" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif-display text-2xl text-[#2E332C]">Mensagens do WhatsApp</h2>
          </div>
          <Button
            onClick={saveMessages}
            disabled={savingMsg}
            className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white px-6"
            data-testid="save-messages"
          >
            {savingMsg ? "Salvando…" : "Salvar todas"}
          </Button>
        </div>
        <p className="text-sm text-[#5C6658] leading-relaxed mb-6">
          Personalize o texto enviado em cada situação. Use as variáveis abaixo para inserir dados automaticamente —
          tudo entre chaves <code className="bg-[#F1EEE3] px-1.5 py-0.5 rounded text-xs">{"{nome}"}</code> será substituído na hora.
          Se a variável estiver vazia (ex.: cliente sem local), a linha é removida automaticamente.
        </p>

        <div className="space-y-6">
          {MESSAGE_FIELDS.map((f) => (
            <div key={f.key} className="border-t border-[#E8E4D9] pt-5 first:border-t-0 first:pt-0">
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <Label className="text-xs uppercase tracking-wider text-[#2E332C] font-semibold">{f.label}</Label>
                  <p className="text-xs text-[#5C6658] mt-1">{f.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => resetMessage(f.key)}
                  className="text-xs text-[#7B8B6F] hover:text-[#647259] flex items-center gap-1"
                  data-testid={`reset-msg-${f.key}`}
                  title="Restaurar mensagem padrão"
                >
                  <RotateCcw className="w-3 h-3" /> Restaurar padrão
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {f.vars.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => navigator.clipboard.writeText(v).then(() => toast.success(`${v} copiado`))}
                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#F4E8D6]/60 text-[#7B5E2E] border border-[#C4A77D]/30 hover:bg-[#F4E8D6]"
                    title="Clique para copiar"
                  >
                    {v}
                  </button>
                ))}
              </div>
              <Textarea
                value={messages[f.key] || ""}
                onChange={(e) => setMessages({ ...messages, [f.key]: e.target.value })}
                rows={f.rows}
                className="mt-2 bg-white font-mono text-sm"
                data-testid={`msg-${f.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="elegant-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#E5EBE0] flex items-center justify-center">
              <Database className="w-5 h-5 text-[#7B8B6F]" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif-display text-2xl text-[#2E332C]">Backup</h2>
          </div>          <p className="text-sm text-[#5C6658] leading-relaxed mb-6">
            Faça download de um arquivo JSON com todos os clientes, tatuagens, números, sorteios e lembretes.
            Você pode restaurar tudo a partir desse arquivo quando quiser.
          </p>

          <div className="space-y-4">
            <Button onClick={doExport} className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white w-full py-5" data-testid="backup-export">
              <Download className="w-4 h-4 mr-2" /> Exportar backup
            </Button>

            <div className="border-t border-[#E8E4D9] pt-4">
              <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Importar backup</Label>
              <Input
                ref={fileRef}
                type="file"
                accept="application/json"
                onChange={doImport}
                disabled={importing}
                className="mt-2 bg-white"
                data-testid="backup-import"
              />
              <label className="flex items-center gap-2 mt-3 text-sm text-[#5C6658]">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="backup-replace" />
                Substituir todos os dados atuais (cuidado!)
              </label>
              {!replace && <p className="text-xs text-[#5C6658] mt-2 italic">Sem substituir: registros existentes serão atualizados; novos serão adicionados.</p>}
            </div>
          </div>
        </div>

        <div className="elegant-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#F4E8D6] flex items-center justify-center">
              <Share2 className="w-5 h-5 text-[#C4A77D]" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif-display text-2xl text-[#2E332C]">Página pública de Ranking</h2>
          </div>
          <p className="text-sm text-[#5C6658] leading-relaxed mb-6">
            Compartilhe o ranking das sortudas no seu Instagram ou stories. Cada cliente vê seus números acumulados —
            vira conteúdo orgânico e atrai novas clientes para "pegar o próximo número".
          </p>

          <div className="rounded-xl bg-[#F5F2E8] p-4 mb-4">
            <p className="text-[10px] uppercase tracking-wider text-[#5C6658]">Link público</p>
            <p className="text-sm text-[#2E332C] truncate" data-testid="ranking-url">{rankingUrl}</p>
          </div>

          <div className="flex gap-2">
            <Button asChild className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white flex-1" data-testid="open-ranking">
              <Link to="/ranking" target="_blank"><ExternalLink className="w-4 h-4 mr-2" /> Abrir página</Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => { navigator.clipboard.writeText(rankingUrl); toast.success("Link copiado"); }}
              className="rounded-full border-[#E8E4D9]"
              data-testid="copy-ranking"
            >
              <Upload className="w-4 h-4 mr-2" /> Copiar
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
